<template>
  <div class="your-employees-to-inspect-table-wrapper">
    <v-row class="row ps-4 mb-4 top-section-class mt-5">
      <v-col class="search-inspection-item">
        <SearchByModal
          @onSearch="$emit('onSearch', $event)"
          @onMultipleFieldSearch="$emit('onMultipleFieldSearch', $event)"
          label="Filters"
          :filterBtn="false"
          :chipText="[]"
          ghostText="Search Employee"
          type="button"
          btnText="Add Employee"
          :valueSearch="filterText"
          @onClick="$emit('onClick', $event)"
          @removeFilter="$emit('removeFilter', $event)"
          :counterSection="false"
          @filterTypes="handleLevelsChange"
          :removeCounterLevelFilter="true"
          :aiAction="{
            enabled: false,
            objectName: 'Inspection',
            theme: 'strong',
          }"
          :filterGroups="filterGroups"
          :filterType="filterType"
          :statusOptions="statusOptions"
          :groupsItems="combinedGroupsItems"
          :defaultSelected="defaultSelected"
          @clearSelection="$emit('clearSelection', $event)"
          @onCheckedItemsChanged="$emit('checkedItemsChanged', $event)"
          :readinessSection="true"
          :showNovice="true"
          :levelCounter="levelCounter"
          :searchPositionByMultipleFields="searchEmployeesAsACoachFieldDefinitions"
          :showSearchPositionByMultipleFieldBox="true"
          searchInstructions="Search for Employee and/or Responsibility"
        />
      </v-col>
    </v-row>
    <NoRecordFound
      v-if="props.employeesData.length === 0"
      title="Employees"
      message="Employees"
      btnTitle="Employee"
      :should-show-add-action="false"
      :description="noRecordDescription"
      class="no-record-found"
      :noBorder="false"
      :showActiveCounter="false"
      :showSpecificMessage="noRecordSpecificMessage"
      :showClearAction="shouldShowClearAction"
      :clearLabel="clearActionText"
      @onClear="handleClear"
    />
    <DxDataGrid
      v-else
      id="ex-employees-datagrid"
      :data-source="paginatedData"
      :column-auto-width="true"
      :word-wrap-enabled="true"
      :show-row-lines="true"
      :show-column-lines="false"
      :show-borders="false"
      :filter-value="[]"
      :element-attr="{ class: 'ex-employees-table-grid' }"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      column-resizing-mode="widget"
      :allow-sorting="false"
      no-data-text=""
      :loading-enabled="false"
      :loadPanel="{ enabled: false }"
      @content-ready="handleContentReady"
    >
      <DxStateStoring
        :enabled="true"
        type="localStorage"
        storage-key="your-employees-to-inspect-table-state-v2"
      />
      <DxRowDragging
        :allow-reordering="false"
        :on-reorder="() => {}"
        :show-drag-icons="true"
      />
      <DxColumn
        data-field="Employees"
        caption="Employees"
        cell-template="employees"
        :width="260"
        :min-width="260"
        :max-width="260"
        :allow-sorting="false"
      />
      <DxColumn
        data-field="ResponsibilityAssignments"
        caption="Responsibility Assignments"
        cell-template="responsibilityAssignments"
        :width="500"
        :allow-sorting="false"
      />
      <DxColumn data-field="Badge" caption="Badge" cell-template="badge" :min-width="200" :allow-sorting="false" />
      <DxColumn
        data-field="InspectionStatus"
        caption="Inspection"
        cell-template="inspectionStatus"
        :min-width="160"
        :allow-sorting="false"
      />
      <!-- Temporarily hidden chatter column -->
      <!-- <DxColumn data-field="Chatter" caption="" cell-template="chatter" :min-width="80" :allow-sorting="false" /> -->
      <template #employees="{ data }">
        <EmployeeCard
          :firstName="data.data.user?.name?.split(' ')[0]"
          :lastName="data.data.user?.name?.split(' ')[1]"
          :photoUrl="data.data.user?.avatar"
          :position="data.data.responsibilityAssignments?.position_name"
          :showAvailability="false"
        />
      </template>
      <template #responsibilityAssignments="{ data }">
        <div class="responsibility-assignments-container">
          <ResponsibilityAssignmentDropDown
            :responsibilityCode="
              data.data.responsibilityAssignments.responsibility_code
            "
            :responsibilityName="
              data.data.responsibilityAssignments.responsibility_name
            "
            :responsibilityImportance="
              data.data.responsibilityAssignments.responsibility_importance
            "
            :roleCode="data.data.responsibilityAssignments.role_code"
            :roleName="data.data.responsibilityAssignments.role_name"
            :roleImportance="
              data.data.responsibilityAssignments.role_importance
            "
            :positionCode="data.data.responsibilityAssignments.position_code"
            :positionName="data.data.responsibilityAssignments.position_name"
            :positionImportance="
              data.data.responsibilityAssignments.position_importance
            "
            :positionCommitment="
              data.data.responsibilityAssignments.position_commitment
            "
            :assignedOn="
              new Date(data.data.responsibilityAssignments.assigned_on)
            "
            :newDuration="
              data.data.responsibilityAssignments.new_duration || 15
            "
            :show_codes="true"
          />
        </div>
      </template>
      <template #badge="{ data }">
        <div>
         <ReadinessBadge
           :badge-id="data.data.badge?.id || data.data.badge_id || data.data.id"
           :level="data.data.badge?.level"
           :dot="formatBadgeStatus(data.data.badge?.status)"
           :view="
             data.data.badge && data.data.badge.level !== 'Novice'
               ? 'Editable'
               : 'Null'
           "
           :expiration_date="toDateOrNull(data.data.badge?.expiration_date)"
           :expires_in_days_from_becoming_obsolete="
             data.data.badge?.expires_in_days_from_becoming_obsolete ?? null
           "
           :has_expiration_if_obsolete="!!data.data.badge?.has_expiration_if_obsolete"
           :hideChatterIcon="true"
           @removeBadge="handleRemoveBadgeClick(data)"
           @expiration-synced="emit('expirationSynced')"
         />
        </div>
      </template>
      <template #inspectionStatus="{ data }">
        <div>
          <IssueBadgeButton
            v-if="getIssueBadgeButtonConfig(data).label"
            v-bind="getIssueBadgeButtonConfig(data)"
            @onClick="handleIssueBadgeClick(data)"
          />
        <InspectionIssueBadge
          :currentBadgeLevel="data.data.badge?.level?.toLowerCase() || 'novice'"
          :checkpoints="data.data.badge?.checkpoints || []"
          :currentBadgeStatus="formatInspectionIssueBadgeStatus(data.data.badge?.status)"
          :useCountsVariant="!!data.data.checkpointCounts"
          :checkpointCounts="data.data.checkpointCounts || {}"
          :isMagentaHover="false"
        />
        </div>
      </template>
      <!-- Temporarily hidden chatter template -->
      <!-- <template #chatter="{ data }">
        <ChatterButton @onClick="() => {}" />
      </template> -->
    </DxDataGrid>
    <ExPagination
      v-if="showPagination"
      :pagination="pagination"
      @onRequest="$emit('onPaginationRequest', $event)"
    />
    <RemoveBadgeDialog
      v-model="showRemoveBadgeDialog"
      :badge-name="selectedBadgeInfo?.badgeName || ''"
      :responsibility-name="selectedBadgeInfo?.responsibilityName || ''"
      :employee-name="selectedBadgeInfo?.employeeName || ''"
      @remove="confirmRemoveBadge"
      @close="handleCloseRemoveBadgeDialog"
    />
  </div>
</template>

<script setup>
import { computed, ref, getCurrentInstance } from "vue";
import SearchByModal from "@components/SearchBar/SearchByModal.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import { DxDataGrid, DxColumn, DxRowDragging, DxStateStoring } from "devextreme-vue/data-grid";
import EmployeeCard from "@components/organizations/employees/EmployeeCard/EmployeeCard.vue";
import ReadinessBadge from "@components/ReadinessBadge/ReadinessBadge.vue";
import InspectionIssueBadge from "@components/InspectionIssueBadge/InspectionIssueBadge.vue";
import IssueBadgeButton from "@components/IssueBadgeButton/IssueBadgeButton.vue";
import ChatterButton from "@components/ChatterButton/ChatterButton.vue";
import RemoveBadgeDialog from "@components/RemoveBadgeDialog/RemoveBadgeDialog.vue";
import { deleteResponsibilityBadge } from "@/api";
import ResponsibilityAssignmentDropDown from "@components/ResponsibilityAssignmentDropDown/ResponsibilityAssignmentDropDown.vue";

const { proxy } = getCurrentInstance();

const props = defineProps({
  employeesData: {
    type: Array,
    default: () => [],
  },
  filterText: {
    type: String,
    default: "",
  },
  showNoRecords: {
    type: Boolean,
    default: false,
  },
  showPagination: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Object,
    default: () => ({
      total: 0,
      page: 1,
      perPage: 10,
    }),
  },
  chipText: {
    type: Array,
    default: () => [],
  },
  filterGroups: {
    type: Array,
    default: () => [],
  },
  filterType: {
    type: String,
    default: "Employees",
  },
  statusOptions: {
    type: Array,
    default: () => [],
  },
  groupsItems: {
    type: Array,
    default: () => [],
  },
  readinessLevelFilterGroups: {
    type: Array,
    default: () => [],
  },
  defaultSelected: {
    type: Array,
    default: () => [],
  },
  levelCounter: {
    type: Object,
    default: () => ({
      Novice: 0,
      Apprentice: 0,
      Professional: 0,
      Coach: 0,
      Master: 0
    }),
  },
});

// Determine if we should show the clear action
const shouldShowClearAction = computed(() => {
  const hasSearch = !!props.filterText;
  const hasFilters = (props.chipText && props.chipText.length > 0) || (props.defaultSelected && props.defaultSelected.length > 0);
  return hasSearch || hasFilters;
});

// Determine the clear button text based on what's active
const clearActionText = computed(() => {
  const hasSearch = !!props.filterText;
  const hasFilters = (props.chipText && props.chipText.length > 0) || (props.defaultSelected && props.defaultSelected.length > 0);
  
  if (hasSearch && hasFilters) {
    return 'Clear All';
  } else if (hasSearch) {
    return 'Clear Search';
  } else if (hasFilters) {
    return 'Clear Filter';
  }
  return 'Clear';
});

const noRecordDescription = computed(() => {
  const hasSearch = !!props.filterText;
  const hasFilters = (props.chipText && props.chipText.length > 0) || (props.defaultSelected && props.defaultSelected.length > 0);

  if (hasSearch && hasFilters) {
    return 'No results found for your search and filters';
  } else if (hasSearch) {
    return 'No results found for your search';
  } else if (hasFilters) {
    return 'No results found for your filters';
  }
  return 'There are no Employees with Assigned Responsibilities that you are a coach of';
});

const noRecordSpecificMessage = computed(() => {
  const hasSearch = !!props.filterText;
  const hasFilters = (props.chipText && props.chipText.length > 0) || (props.defaultSelected && props.defaultSelected.length > 0);

  if (hasSearch || hasFilters) {
    return 'Try adjusting your search criteria';
  }
  return 'You currently have no Employees to Inspect';
});

// Handle clear action
const handleClear = () => {
  const hasSearch = !!props.filterText;
  const hasFilters = (props.chipText && props.chipText.length > 0) || (props.defaultSelected && props.defaultSelected.length > 0);
  
  // Show loader immediately to prevent "no records" flash
  proxy.$loader.show();
  
  if (hasSearch) {
    emit('onSearch', '');
  }
  
  if (hasFilters) {
    emit('clearSelection');
  }
};

const getBadgeLevel = (level) => {
  const levels = ["Novice", "Apprentice", "Professional", "Coach", "Master"];
  if (levels.includes(level)) {
    return level;
  }
  if (typeof level === 'string') {
    return level.charAt(0).toUpperCase() + level.slice(1);
  }
  return levels[level] || "Novice";
};

const formatBadgeStatus = (status) => {
  if (!status) return 'No Dot';
  
  const statusMap = {
    'good_standing': 'Good Standing',
    'given': 'Given',
    'obsolete': 'Obsolete',
    'no_dot': 'No Dot'
  };
  
  return statusMap[status] || status;
};

const formatInspectionIssueBadgeStatus = (status) => {
  if (!status) return 'null';
  
  const statusMap = {
    'good_standing': 'good_standing',
    'given': 'forced',
    'obsolete': 'obsolete',
    'no_dot': 'null'
  };
  
  return statusMap[status] || 'null';
};

const toDateOrNull = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const issueBadgeAction = (data) => {
  const currentBadge = data.data?.badge;
  const checkpoints = currentBadge?.checkpoints || [];
  const checkpointCounts = data.data?.checkpointCounts;

  let activeCheckpointsCount = 0;
  
  if (checkpointCounts) {
    const levels = ['apprentice', 'professional', 'coach', 'master'];
    activeCheckpointsCount = levels.reduce((total, level) => {
      return total + (checkpointCounts[`${level}_checkpoint_count`] || 0);
    }, 0);
  } else {
    activeCheckpointsCount = checkpoints.filter(
      (checkpoint) => checkpoint.status === "active"
    ).length;
  }

  if (currentBadge && getBadgeLevel(currentBadge.level) === "Master") {
    return "No Allowed";
  }

  if (
    !currentBadge ||
    getBadgeLevel(currentBadge.level) === "Novice"
  ) {
    if (activeCheckpointsCount === 0) {
      return "Give";
    } else {
      return "Inspect";
    }
  }

  if (currentBadge && getBadgeLevel(currentBadge.level) !== "Master") {
    if (activeCheckpointsCount === 0) {
      return "Give Upgrade";
    } else {
      return "Inspect";
    }
  }

  return "Inspect";
};

const getIssueBadgeButtonConfig = (data) => {
  const action = issueBadgeAction(data);

  if (action === "Give") {
    return {
      label: 'Give Badge',
      tooltipText: 'Responsibility has no Checklist. You can only give badges',
      backgroundColor: '#000000',
      buttonType: 'primary',
      size: 'medium'
    };
  }

  if (action === "Give Upgrade") {
    return {
      label: 'Upgrade Badge',
      tooltipText: 'Responsibility has no Checklist. You can now only give an upgrade to the current badge',
      backgroundColor: '#000000',
      buttonType: 'primary',
      size: 'medium'
    };
  }

  if (action === "Inspect") {
    return {
      label: 'Inspect and Award',
      tooltipText: 'Click to open Inspection Form',
      backgroundColor: '#7C3AED',
      buttonType: 'primary',
      size: 'medium'
    };
  }

  return {
    label: '',
    tooltipText: '',
    backgroundColor: '#7C3AED',
    buttonType: 'primary',
    size: 'medium'
  };
};

const showRemoveBadgeDialog = ref(false);
const selectedBadge = ref(null);
const selectedEmployeeData = ref(null);
const selectedBadgeInfo = ref(null);

const emit = defineEmits([
  "onSearch",
  "onMultipleFieldSearch",
  "onClick",
  "removeFilter",
  "filterTypes",
  "onPaginationRequest",
  "clearSelection",
  "checkedItemsChanged",
  "onIssueBadgeClick",
  "onBadgeRemoved",
  "expirationSynced",
]);

/** Field definitions for SearchPositionByMultipleFieldBox (key + label). */
const searchEmployeesAsACoachFieldDefinitions = [
  { key: "employee_name", label: "Employee name" },
  { key: "responsibility_name", label: "Responsibility name" },
];

const handleIssueBadgeClick = (data) => {
  const action = issueBadgeAction(data);
  emit('onIssueBadgeClick', { data: data.data, action });
};

const handleLevelsChange = (levels) => {
  emit('onLevelsChange', levels);
};

const handleRemoveBadgeClick = (data) => {
  const badge = data.data.badge;
  selectedBadge.value = badge;
  selectedEmployeeData.value = data.data;
  selectedBadgeInfo.value = {
    badgeName: badge ? getBadgeLevel(badge.level) : "",
    responsibilityName: data.data.responsibilityAssignments?.responsibility_name || "",
    employeeName: data.data.user?.name || "",
  };
  showRemoveBadgeDialog.value = true;
};

const handleCloseRemoveBadgeDialog = () => {
  showRemoveBadgeDialog.value = false;
  selectedBadge.value = null;
  selectedEmployeeData.value = null;
  selectedBadgeInfo.value = null;
};

const confirmRemoveBadge = async ({ reason: removeReason }) => {
  if (!selectedBadge.value || !removeReason?.trim()) return;

  try {
    proxy.$loader.setAppLoading(true);
    const payload = {
      badge: selectedBadge.value.id,
      method: "removed",
      reason: removeReason.trim(),
    };
    const accountID = localStorage.getItem("currentAccountID");
    const badgeId = selectedBadge.value.id;

    await deleteResponsibilityBadge(accountID, badgeId, payload);

    emit("onBadgeRemoved", {
      employeeData: selectedEmployeeData.value,
      badgeId: selectedBadge.value.id,
    });

    handleCloseRemoveBadgeDialog();
  } catch (error) {
    console.error("Error removing badge: ", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const readinessLevelFilterGroups = computed(() => {
  return [
    {
      groups: [
        {
          type: "check",
          label: "Readiness Level",
          items: [
            { label: "Novice", value: "novice" },
            { label: "Apprentice", value: "apprentice" },
            { label: "Professional", value: "professional" }
          ]
        }
      ]
    }
  ];
});

const combinedGroupsItems = computed(() => {
  return [...props.groupsItems, ...readinessLevelFilterGroups.value];
});

const paginatedData = computed(() => {
  return props.employeesData;
});

const handleContentReady = () => {
};
</script>

<style scoped>
.your-employees-to-inspect-table-wrapper {
  background: #fff;
  width: 100%;
}

.your-employees-to-inspect-table-wrapper
  ::v-deep
  .dx-datagrid-content
  .dx-datagrid-table
  .dx-row
  > td,
.your-employees-to-inspect-table-wrapper
  ::v-deep
  .dx-datagrid-content
  .dx-datagrid-table
  .dx-row
  > tr
  > td {
  vertical-align: middle;
  padding: 12px 16px;
  height: auto !important;
}

.your-employees-to-inspect-table-wrapper
  ::v-deep
  .dx-datagrid-content
  .dx-datagrid-table
  .dx-header-row
  > td,
.your-employees-to-inspect-table-wrapper
  ::v-deep
  .dx-datagrid-content
  .dx-datagrid-table
  .dx-header-row
  > tr
  > td {
  padding: 13px 24px !important;
}

.your-employees-to-inspect-table-wrapper ::v-deep .dx-datagrid {
  background: #fff; 
  width: 100%;
}

.your-employees-to-inspect-table-wrapper ::v-deep .dx-datagrid-headers {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  background-color: #f9fafb;
}

.your-employees-to-inspect-table-wrapper
  ::v-deep
  .dx-datagrid-headers
  .dx-datagrid-table
  .dx-row
  > td,
.your-employees-to-inspect-table-wrapper
  ::v-deep
  .dx-datagrid
  .dx-row-lines
  > td {
  border-bottom: 1px solid #eaecf0 !important;
}

.your-employees-to-inspect-table-wrapper ::v-deep .dx-datagrid-headers {
  border-top: 1px solid #eaecf0;
}

.your-employees-to-inspect-table-wrapper ::v-deep .dx-datagrid-rowsview {
  border-top: none;
  overflow-x: auto !important;
  overflow-y: auto !important;
}

.search-inspection-item {
  margin-top: -12px;
}

.top-section-class {
  margin-right: -9px;
}

::v-deep .no-record-found .wrapper {
  border: none !important;
}

::v-deep .employee-card-container {
    border: none !important;
    width: 100%;
    max-width: 100%;
}

::v-deep .employee-name-container {
    flex: 1 !important;
    overflow: hidden;
}

::v-deep .employee-name-container > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

::v-deep .dx-datagrid-content .dx-datagrid-table .dx-row > td {
    width: auto !important;
    padding: 12px 16px !important;
}

/* Fix Employee (first) column to 260 - overrides width: auto on first cell */
.your-employees-to-inspect-table-wrapper ::v-deep .dx-datagrid-content .dx-datagrid-table .dx-row > td:first-child,
.your-employees-to-inspect-table-wrapper ::v-deep .dx-datagrid-headers .dx-datagrid-table .dx-row > td:first-child {
  width: 260px !important;
  min-width: 260px !important;
  max-width: 260px !important;
}

::v-deep .dx-datagrid-content .dx-datagrid-table .dx-row > td:nth-child(3) {
    text-align: left !important;
}

::v-deep .dx-datagrid-content .dx-datagrid-table .dx-row > td:nth-child(3) > * {
    display: flex !important;
    justify-content: flex-start !important;
}

::v-deep .dx-datagrid {
    width: 100% !important;
    max-width: 100% !important;
}

::v-deep .responsibility-assignments-container {
    width: 100%;
    max-width: 500px;
    display: flex;
    align-items: flex-start;
    padding: 4px 0;
}

::v-deep .res-assignment-card {
    width: 100% !important;
    max-width: 100% !important;
}

::v-deep .dx-datagrid-rowsview .dx-row {
    height: auto !important;
}

::v-deep .dx-datagrid-content .dx-datagrid-table .dx-row {
    height: auto !important;
}

::v-deep .issue-badge-button {
    overflow: visible !important;
    text-overflow: clip !important;
    max-width: none !important;
    width: auto !important;
}

::v-deep .issue-badge-button span {
    overflow: visible !important;
    text-overflow: clip !important;
}

::v-deep .issue-badge-padding-container-without-hover {
  padding: 8px 0px !important;
}

::v-deep .responsibility-assignments-container .rad__trigger-btn {
  width: 100% !important;
}
</style>
